import { useEffect, useRef } from 'react';

// De terugknop van het toestel (of de browser) gebruiken om in de app een scherm terug te gaan,
// i.p.v. dat de hele app sluit. De router kent alleen de routes (dashboard, upgrade, login, ...).
// Tabbladen, subtabs, popups en uitgeklapte kaartjes zijn gewone React-state, dus de browser kent
// daarvoor geen geschiedenis om naar terug te gaan.
//
// Elk "dieper" scherm of elke popup meldt zich hier aan als laag (useTerugDiepte). Per laag
// staat er een geschiedenisregel klaar. Drukt de gebruiker op terug, dan sluit de bovenste laag
// zichzelf. Zonder lagen (het hoogste niveau, de agenda) valt er niets op te vangen en sluit de
// terugknop de app, zoals elke app dat doet.
//
// Het aantal geschiedenisregels volgt altijd het aantal aangemelde lagen. Sluit een scherm via
// zijn eigen knop in de app, dan haalt synchroniseer() de overbodige regel weer weg, zodat de
// terugknop van het toestel nooit "leeg" aanvoelt.
//
// Er draait ook een router (react-router). Daarom nemen onze regels de bestaande state van de
// router mee (idx, key, usr) en springt synchroniseer() nooit terug als de router intussen naar
// een andere pagina is genavigeerd. Anders zou het sluiten van een laag tijdens een navigatie
// (bv. de knop Upgrade met een tabblad open) de gebruiker weer van die nieuwe pagina afhalen.

const lagen = [];
// Aantal door ons gezette geschiedenisregels dat nu bovenop de basisregel staat.
let geschiedenisDiepte = 0;
// history.go() is asynchroon, tot het bijhorende popstate-event binnen is mag er niets gepusht
// worden (anders raken beide door elkaar).
let wachtOpTerugspringen = false;
let ingepland = false;

function leesDiepte(state) {
    const diepte = state && state.ftDiepte;
    return typeof diepte === 'number' && diepte > 0 ? diepte : 0;
}

function synchroniseer() {
    if (wachtOpTerugspringen) return;
    // Staat de huidige regel niet (meer) op onze diepte, dan heeft de router een eigen regel
    // gezet of vervangen. Onze teller volgt dan de werkelijke regel en we springen niet terug.
    geschiedenisDiepte = leesDiepte(window.history.state);
    const doel = lagen.length;
    if (geschiedenisDiepte < doel) {
        while (geschiedenisDiepte < doel) {
            geschiedenisDiepte += 1;
            window.history.pushState({ ...window.history.state, ftDiepte: geschiedenisDiepte }, '');
        }
    } else if (geschiedenisDiepte > doel) {
        const stappen = geschiedenisDiepte - doel;
        geschiedenisDiepte = doel;
        wachtOpTerugspringen = true;
        window.history.go(-stappen);
        // Vangnet voor het geval het popstate-event nooit aankomt.
        window.setTimeout(() => {
            if (!wachtOpTerugspringen) return;
            wachtOpTerugspringen = false;
            inplannen();
        }, 500);
    }
}

// Gebundeld per ronde, zodat een scherm dat sluit terwijl er meteen een ander opent (bv. van
// tabblad wisselen) geen onnodige geschiedenisbewerkingen veroorzaakt.
function inplannen() {
    if (ingepland) return;
    ingepland = true;
    queueMicrotask(() => {
        ingepland = false;
        synchroniseer();
    });
}

function naTerug(event) {
    const diepte = leesDiepte(event.state);
    if (diepte === geschiedenisDiepte) {
        // Onze eigen opruiming (history.go in synchroniseer) is klaar, of de router liep
        // tussen twee regels zonder diepte.
        wachtOpTerugspringen = false;
        inplannen();
        return;
    }
    const oudeDiepte = geschiedenisDiepte;
    geschiedenisDiepte = diepte;
    wachtOpTerugspringen = false;
    if (diepte < oudeDiepte && lagen.length > 0) {
        // De gebruiker drukte op terug, alleen de bovenste laag sluit.
        lagen[lagen.length - 1].sluit();
    }
    // Na het sluiten (React heeft dan de laag afgemeld) kloppen aantal lagen en regels weer.
    // Weigerde de laag te sluiten, dan zet dit de ontbrekende geschiedenisregel terug.
    window.setTimeout(inplannen, 150);
}

if (typeof window !== 'undefined') {
    // Na een herlading kan de huidige regel nog een diepte uit de vorige sessie dragen, terwijl
    // die lagen nu niet meer bestaan. De state van de router blijft bewaard.
    if (leesDiepte(window.history.state) > 0) {
        const { ftDiepte, ...rest } = window.history.state;
        window.history.replaceState(Object.keys(rest).length > 0 ? rest : null, '');
    }
    window.addEventListener('popstate', naTerug);
}

/**
 * Meldt `diepte` lagen aan zolang dit scherm of deze popup open staat. Een druk op de terugknop
 * van het toestel roept `terug` een keer aan, die moet het scherm een niveau terugzetten (dus
 * `diepte` met 1 verlagen). Geef 0 als er niets te sluiten valt.
 */
export function useTerugDiepte(diepte, terug) {
    const terugRef = useRef(terug);
    terugRef.current = terug;
    const eigen = useRef([]);
    useEffect(() => {
        while (eigen.current.length < diepte) {
            const laag = { sluit: () => terugRef.current() };
            eigen.current.push(laag);
            lagen.push(laag);
        }
        while (eigen.current.length > diepte) {
            const laag = eigen.current.pop();
            const positie = laag ? lagen.indexOf(laag) : -1;
            if (positie >= 0) lagen.splice(positie, 1);
        }
        inplannen();
    }, [diepte]);
    // Bij het verdwijnen van het scherm moeten de lagen weg die er op dat moment zijn, dus
    // eigen.current wordt bewust pas in de opruiming gelezen.
    useEffect(
        () => () => {
            for (const laag of eigen.current) {
                const positie = lagen.indexOf(laag);
                if (positie >= 0) lagen.splice(positie, 1);
            }
            eigen.current = [];
            inplannen();
        },
        [],
    );
}
